import React, { useState } from 'react'
import styled from 'styled-components'
import { Icon } from '../../atoms/Icon'

const FilterSectionContainer = styled.div<{ $collapsed: boolean }>`
  margin-bottom: 24px;
  background: ${({ theme }) => theme.colors.background.primary};
  border-bottom: 1px solid ${({ theme }) => theme.colors.border.light};
  position: relative;
  transition: all ${({ theme }) => theme.transitions.default};

  &:last-child {
    margin-bottom: 0;
  }

  &:hover {
    border-color: ${({ theme }) => theme.colors.border.default};
  }
`

const FilterSectionHeader = styled.div<{ $collapsed: boolean }>`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: ${({ theme }) => theme.spacing.medium}
    ${({ theme }) => theme.spacing.large};
  cursor: pointer;
  user-select: none;
  transition: background ${({ theme }) => theme.transitions.default};

  ${({ $collapsed, theme }) =>
    $collapsed &&
    `
    &:hover {
      background: ${theme.colors.background.secondary};
    }
  `}
`

const FilterSectionTitle = styled.div`
  font-size: 14px;
  font-weight: 600;
  color: ${({ theme }) => theme.colors.text.primary};
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing.small};
`

const FilterSectionCount = styled.span<{ $active: boolean }>`
  font-size: 12px;
  font-weight: 400;
  color: ${({ theme, $active }) =>
    $active ? theme.colors.primary : theme.colors.text.tertiary};
  margin-left: 4px;
`

const FilterSectionToggle = styled.div<{ $collapsed: boolean }>`
  width: 20px;
  height: 20px;
  color: ${({ theme }) => theme.colors.text.secondary};
  transition: transform ${({ theme }) => theme.transitions.default};
  transform: ${({ $collapsed }) =>
    $collapsed ? 'rotate(-90deg)' : 'rotate(0deg)'};
  display: flex;
  align-items: center;
  justify-content: center;
`

const FilterSectionContent = styled.div<{ $collapsed: boolean }>`
  display: ${({ $collapsed }) => ($collapsed ? 'none' : 'block')};
  padding: ${({ theme }) => theme.spacing.large};
  border-top: 1px solid ${({ theme }) => theme.colors.border.light};
  background: ${({ theme }) => theme.colors.background.primary};
  position: relative;
  overflow: visible;
  animation: ${({ $collapsed }) =>
    $collapsed ? 'none' : 'slideDown 0.2s ease-out'};

  @keyframes slideDown {
    from {
      opacity: 0;
      transform: translateY(-10px);
    }
    to {
      opacity: 1;
      transform: translateY(0);
    }
  }
`

interface FilterSectionProps {
  title: string
  icon: React.ReactNode
  count?: number | string
  defaultCollapsed?: boolean
  children: React.ReactNode
  active?: boolean
}

export const FilterSection: React.FC<FilterSectionProps> = ({
  title,
  icon,
  count,
  defaultCollapsed = true,
  children,
  active = false,
  ...props
}) => {
  const [collapsed, setCollapsed] = useState(defaultCollapsed)

  return (
    <FilterSectionContainer $collapsed={collapsed} {...props}>
      <FilterSectionHeader
        $collapsed={collapsed}
        onClick={() => setCollapsed(!collapsed)}
      >
        <FilterSectionTitle>
          {icon}
          {title}
          {count !== undefined && (
            <FilterSectionCount $active={active}>({count})</FilterSectionCount>
          )}
        </FilterSectionTitle>
        <FilterSectionToggle $collapsed={collapsed}>
          <Icon name={collapsed ? 'chevron-down' : 'chevron-up'} size="small" />
        </FilterSectionToggle>
      </FilterSectionHeader>
      <FilterSectionContent $collapsed={collapsed}>
        {children}
      </FilterSectionContent>
    </FilterSectionContainer>
  )
}
